import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SEO Component - Handles meta tags, Open Graph, Twitter Cards, and Schema.org
 */
function SEO({
  title,
  description,
  image,
  url,
  type = 'website',
  schema = null,
  product = null,
  category = null,
  siteName = 'Industrial Supply Marketplace',
  author = 'Industrial Supply Marketplace'
}) {
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const fullUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const fullImage = image?.startsWith('http') ? image : `${siteUrl}${image || '/favicon.ico'}`;

  // Generate schema.org structured data
  const generateSchema = () => {
    if (schema) return schema;

    // Product schema
    if (product && type === 'product') {
      const productSchema = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: product.name,
        description: product.short_description || product.description || description,
        image: product.images?.[0]?.src ? `${siteUrl}${product.images[0].src}` : fullImage,
        sku: product.sku,
        brand: {
          '@type': 'Brand',
          name: product.attributes?.find(a => a.name.toLowerCase().includes('brand'))?.value?.trim() ||
                product.attributes?.find(a => a.name.toLowerCase().includes('manufacturer'))?.value?.trim() ||
                'Industrial Supply'
        },
        offers: {
          '@type': 'Offer',
          price: product.sale_price || product.regular_price || '0.00',
          priceCurrency: 'USD',
          availability: product.stock_status === 'instock' ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
          url: fullUrl,
          seller: {
            '@type': 'Organization',
            name: siteName
          }
        }
      };

      // Add aggregateRating if available
      if (product.rating_count && product.average_rating) {
        productSchema.aggregateRating = {
          '@type': 'AggregateRating',
          ratingValue: product.average_rating,
          reviewCount: product.rating_count
        };
      }

      return productSchema;
    }

    // Category/Collection schema
    if (category && type === 'collection') {
      return {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: category.name,
        description: description,
        url: fullUrl,
        mainEntity: {
          '@type': 'ItemList',
          numberOfItems: category.product_count || 0,
          itemListElement: (category.products || []).slice(0, 10).map((product, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            item: {
              '@type': 'Product',
              name: product.name,
              url: `${siteUrl}/products/${product.slug}`,
              image: product.images?.[0]?.src ? `${siteUrl}${product.images[0].src}` : undefined
            }
          }))
        }
      };
    }

    // Organization schema (homepage)
    return {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: siteName,
      url: siteUrl,
      logo: `${siteUrl}/favicon.ico`,
      description: description,
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+1-727-777-7525',
        contactType: 'Customer Service',
        areaServed: 'US',
        availableLanguage: 'English'
      },
      sameAs: []
    };
  };

  const schemaData = generateSchema();

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type === 'product' ? 'product' : 'website'} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_US" />

      {/* Product-specific Open Graph */}
      {product && (
        <>
          <meta property="product:price:amount" content={product.sale_price || product.regular_price || '0.00'} />
          <meta property="product:price:currency" content="USD" />
          <meta property="product:availability" content={product.stock_status === 'instock' ? 'in stock' : 'out of stock'} />
          {product.sku && <meta property="product:retailer_item_id" content={product.sku} />}
        </>
      )}

      {/* Twitter Card */}
      <meta name="twitter:card" content={type === 'product' ? 'summary_large_image' : 'summary'} />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />

      {/* Additional SEO Tags */}
      <meta name="author" content={author} />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />

      {/* Schema.org JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(schemaData)}
      </script>
    </Helmet>
  );
}

export default SEO;
